import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { X, Save, Info } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle, faCircle, faList } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createProvider, updateProvider, fetchProvider, clearCurrentProvider } from '../../../../store/slices/providerSlice';
import providerService from '../../../../services/providerService';
import { fetchPractice, addProviderOptimistic, fetchPracticePatients } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import EventLogSection from '../../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../../hooks/useEventLogModalScrollLock';
import './AddProvider.css';
import '../../PatientManagement/AddPatient/AddPatientModal.css';
import api from '../../../../services/api';
import { validatePassword, applyPasswordServerFieldError, formatDisplayError } from '../../../../utils/passwordValidation';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import ReassignmentModal from '../../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import { createModalEscapeKeyDownHandler } from '../../../../utils/modalEscape';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import FormFieldError from '../../../../components/Common/FormFieldError/FormFieldError';

const AddProviderModal = ({ isOpen, onClose, practiceId: propPracticeId, isEditMode: propIsEditMode, editId: propEditId, isSystemAdmin: propIsSystemAdmin, isViewMode: propIsViewMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { currentProvider, isLoading, isUpdating, isCreating } = useSelector(state => state.provider);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const editId = propEditId || searchParams.get("editId");
  const isViewMode = Boolean(propIsViewMode);
  const isEditMode = !isViewMode && (propIsEditMode || Boolean(editId));
  const isRecordMode = isViewMode || isEditMode;
  const savedSuccessfullyRef = useRef(false);

  useEffect(() => {
    if (isOpen && isRecordMode) {
      savedSuccessfullyRef.current = false;
    }
  }, [isOpen, isRecordMode, editId]);

  const handleClose = useCallback(async () => {
    if (isRecordMode && editId && practiceId && !savedSuccessfullyRef.current) {
      try {
        await providerService.logProviderView(practiceId, editId);
      } catch (error) {
        console.error('Failed to log provider view:', error);
      }
    }
    if (onClose) onClose();
  }, [isRecordMode, editId, practiceId, onClose]);
  const excludeUserId = isRecordMode ? (editId ?? currentProvider?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEventLogsModal, setShowEventLogsModal] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showEventLogsModal, logOverlayRef, logContainerRef);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeProviders, setAlternativeProviders] = useState([]);
  const [reassignments, setReassignments] = useState({}); // { patientId: newProviderId }
  const [isReassigning, setIsReassigning] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    mobileAppAccess: 'Disabled',
    timeZone: '',
    status: 'Active'
  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  const [errors, setErrors] = useState({});

  const getProviderSubmissionErrorMessage = (error, fallbackMessage) => {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallbackMessage
    );
  };

  const applyServerFieldError = (message) => {
    if (!message) return false;

    const normalizedMessage = message.toLowerCase();
    const nextFieldErrors = {};

    if (normalizedMessage.includes('email') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate'))) {
      nextFieldErrors.email = message;
    }

    if (normalizedMessage.includes('username') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate') || normalizedMessage.includes('taken'))) {
      nextFieldErrors.username = message;
    }

    if (normalizedMessage.includes('password')) {
      nextFieldErrors.password = message;
    }

    if (Object.keys(nextFieldErrors).length === 0) {
      return false;
    }

    setErrors(prev => ({ ...prev, ...nextFieldErrors }));
    return true;
  };

  // Fetch provider data when in edit mode
  useEffect(() => {
    if (isRecordMode && editId && practiceId) {
      dispatch(clearCurrentProvider());
      dispatch(fetchProvider({ practiceId, providerId: editId }));
    }
  }, [isRecordMode, editId, practiceId, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    const practiceName = currentPractice?.practice_name || 'Practice';
    const fromDashboard = !!location.state?.fromDashboard;
    const rootLabel = fromDashboard ? 'Dashboard' : 'Practices';
    const rootPath = fromDashboard ? '/dashboard' : '/practices';
    const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';


    updateBreadcrumbs([
      { label: rootLabel, path: rootPath },
      { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
      { label: 'Providers', path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=provider${dashboardTabSuffix}` },

      { label: isViewMode ? 'View Provider' : isEditMode ? 'Edit Provider' : 'Add Provider', path: null, active: true }
    ]);

  }, [practiceId, isEditMode, isViewMode, currentPractice, updateBreadcrumbs]);

  // Populate form when provider data is loaded
  useEffect(() => {
    if (isRecordMode && currentProvider && String(currentProvider.id) === String(editId)) {
      const stateId = currentProvider.address?.state_id || currentProvider.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: (currentProvider.title_id !== null && currentProvider.title_id !== undefined)
          ? String(currentProvider.title_id)
          : (TITLES.find(t => t.label === currentProvider.title)?.id ? String(TITLES.find(t => t.label === currentProvider.title).id) : ''),
        firstName: currentProvider.first_name || '',
        middleInitial: currentProvider.middle_name || '',
        lastName: currentProvider.last_name || '',
        username: currentProvider.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentProvider.address?.address_line_1 || currentProvider.address_line_1 || '',
        addressLine2: currentProvider.address?.address_line_2 || currentProvider.address_line_2 || '',
        city: currentProvider.address?.city || currentProvider.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: currentProvider.address?.zip_code || currentProvider.zip_code ? String(currentProvider.address?.zip_code || currentProvider.zip_code) : '',
        homePhone: formatPhoneNumber(currentProvider.cell_phone || ''),
        cellPhone: formatPhoneNumber(currentProvider.phone || ''),
        fax: formatPhoneNumber(currentProvider.fax || ''),
        email: currentProvider.email || '',
        twoWayAuth: Number(currentProvider.is_two_way_auth) === 1 ? 'Enabled' : 'Disabled',
        mobileAppAccess: Number(currentProvider.mobile_app_access) === 1 ? 'Enabled' : 'Disabled',
        timeZone: normalizeTimeZone(currentProvider.time_zone || currentProvider.timeZone || currentProvider.timezone) || '',
        status: currentProvider.status ? (currentProvider.status.charAt(0).toUpperCase() + currentProvider.status.slice(1).toLowerCase()) : 'Active'
      };
      setFormData(mappedData);
      setInitialFormData(mappedData);
      setLoadedCredentials({
        username: mappedData.username || '',
        email: mappedData.email || '',
      });
    }
  }, [isRecordMode, currentProvider, editId]);

  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // State Sync — handled below with error clearing in setFormData

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
      if (value.length > 20) {
        return;
      }
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      // Only perform real-time validation if the user has typed something.
      // If the field is cleared, simply remove the error message.
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // 4. City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 5. Status Change Detection
    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        startInactivationFlow();
        // Keep status unchanged until reassignment/no-patient flow completes.
        return;
      }
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));

      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // If state name changed, sync stateId
      if (name === 'state') {
        const selectedState = US_STATES.find(s => s.name === value);
        if (selectedState) {
          updated.stateId = selectedState.id;
        }
      }

      return updated;
    });

    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Helper function to safely trim string values
    const safeTrim = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    // No confirm password for provider

    // Provider validation removed username/password

    // City alpha validation
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.city)) {
      newErrors.city = 'City can only contain alphabets';
    }

    if (!formData.state?.trim()) newErrors.state = 'State is required';

    // Zip Code
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    // Email validation (trim extra spaces)
    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    // Phone validation (fully filled)
    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    if (!isEditMode && !formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }

    // Username & password validation
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }

    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!formData.status?.trim()) newErrors.status = 'Status is required';
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';

    // Password confirmation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
    }

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  // Debug mount
  useEffect(() => {
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
      const emailOk = await ensureEmailAvailable(formData.email, setErrors);
      if (!usernameOk || !emailOk) {
        setIsSubmitting(false);
        toast.warn(VALIDATION_SUMMARY_MESSAGE);
        return;
      }
      try {
        await saveProvider();
      } catch (error) {
        console.error('Submit catch:', error);
        const errorMessage = getProviderSubmissionErrorMessage(error, '');
        if (errorMessage.toLowerCase().includes('missing') || errorMessage.toLowerCase().includes('required')) {
          toast.warn(VALIDATION_SUMMARY_MESSAGE);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
    }

  };
  const saveProvider = async () => {
    const providerData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleInitial,
      email: (formData.email || '').trim(),
      phone: (formData.cellPhone || '').replace(/\D/g, ''),
      cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
      fax: formData.fax,
      address_line_1: formData.addressLine1,
      address_line_2: formData.addressLine2,
      city: formData.city,
      zip_code: formData.zipCode,
      status: formData.status || 'Active',
      state_id: formData.stateId || 1,
      title_id: formData.title ? parseInt(formData.title) : null,
      username: formData.username?.trim(),
      password: formData.password?.trim() || undefined,
      time_zone: formData.timeZone || '',
      is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
      mobile_app_access: formData.mobileAppAccess === 'Enabled' ? 1 : 0
    };
    try {
      if (isEditMode) {
        if (!providerData.password) delete providerData.password;
        await dispatch(updateProvider({ practiceId, providerId: editId, providerData })).unwrap();
        toast.success('Provider updated successfully');
      } else {
        const tempId = `temp-provider-${Date.now()}`;
        const optimisticProvider = {
          id: tempId,
          name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() || 'N/A',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: (formData.email || '').trim(),
          home_phone: formData.cellPhone || 'N/A',
          cell_phone: formData.homePhone || 'N/A',
          fax: formData.fax || 'N/A',
          status: formData.status || 'Active',
          address: {
            address_line_1: formData.addressLine1 || 'N/A'
          },
          username: formData.username?.trim()
        };

        dispatch(addProviderOptimistic({ practiceId, provider: optimisticProvider }));
        await dispatch(createProvider({ practiceId, providerData, tempId })).unwrap();
        toast.success('Provider added successfully!');
      }
      savedSuccessfullyRef.current = true;
      if (onClose) onClose();
    } catch (error) {
      console.error('Submission error:', error);
      const apiMsg = formatDisplayError(error);
      const lower = apiMsg.toLowerCase();
      let hasUserErr = false;
      let hasEmailErr = false;

      if (lower.includes('username') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken. Please choose a different username.' }));
        hasUserErr = true;
      }
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email.' }));
        hasEmailErr = true;
      }

      if (applyPasswordServerFieldError(setErrors, apiMsg)) {
        return;
      }

      let friendlyMsg = '';
      if (hasUserErr && hasEmailErr) {
        friendlyMsg = 'This username is already taken and this email address is already registered. Please use different values.';
      } else if (hasUserErr) {
        friendlyMsg = 'This username is already taken. Please choose a different username.';
      } else if (hasEmailErr) {
        friendlyMsg = 'This email address is already registered. Please use a different email.';
      } else {
        friendlyMsg = apiMsg || `Failed to ${isEditMode ? 'update' : 'add'} provider. Please try again.`;
      }
      toast.error(friendlyMsg);
    } finally {
      setIsSubmitting(false);
    }
  };


  const startInactivationFlow = async () => {
    setIsSubmitting(true);
    try {
      // 1. Fetch patients for this provider
      const patientsResponse = await api.get(`/practices/${practiceId}/patients`, {
        params: { providerId: editId, limit: 100 }
      });
      const patients = patientsResponse.data?.data?.patients || [];
      setAssignedPatients(patients);

      // 2. Fetch alternative providers
      const providersResponse = await api.get(`/practices/${practiceId}/providers`, { params: { limit: 1000 } });
      const rawProviders =
        providersResponse.data?.data?.providers ||
        providersResponse.data?.providers ||
        providersResponse.data?.data ||
        providersResponse.data ||
        [];
      const providers = Array.isArray(rawProviders) ? rawProviders : [];

      // Filter out the current provider
      setAlternativeProviders(
        providers.filter((p) => {
          const status = p?.status;
          const isActive =
            status === 1 ||
            status === true ||
            (typeof status === 'string' && status.toLowerCase() === 'active');
          return String(p?.id) !== String(editId) && isActive;
        })
      );

      if (patients.length > 0) {
        // Initialize reassignments state
        const initialReassignments = {};
        patients.forEach(p => {
          initialReassignments[p.id] = '';
        });
        setReassignments(initialReassignments);
        setShowReassignmentModal(true);
      } else {
        // No patients, proceed with inactivation
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
        toast.info('No patients assigned. You can now save the provider as Inactive.');
      }
    } catch (error) {
      console.error('Failed to start inactivation flow:', error);
      toast.error('Failed to check assigned patients.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    // Validate that all patients have a new provider selected
    const unassignedCount = Object.values(reassignments).filter(id => !id).length;
    if (unassignedCount > 0) {
      toast.warning(`Please select a new provider for all ${unassignedCount} patients.`);
      return;
    }

    setIsReassigning(true);
    try {
      const payload = {
        reassignments: Object.entries(reassignments).map(([patientId, providerId]) => ({
          patientId,
          providerId
        }))
      };

      await api.post(`/practices/${practiceId}/patients/batch-reassign-provider`, payload);

      setShowReassignmentModal(false);
      setFormData(prev => ({ ...prev, status: 'Inactive' }));
      // Refresh practice patient list immediately so patient table shows updated provider names without full reload.
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
      toast.success('Patients reassigned successfully. Provider status set to Inactive.');
    } catch (error) {
      console.error('Batch reassignment failed:', error);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsReassigning(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = () => {
    const fromDashboard = location.state?.fromDashboard;
    const path = fromDashboard ? `/dashboard/${practiceId}?tab=provider` : `/practices/${practiceId}?tab=provider`;
    navigate(path, { state: { fromDashboard } });
  };


  // Show loading state while fetching provider data in edit mode
  // Focus trap: keeps keyboard navigation inside the modal while open
  const modalRef = useRef(null);
  const handleEscapeKeyDown = createModalEscapeKeyDownHandler(handleClose);
  useFocusTrap(modalRef, !!isOpen, handleClose);

  if (!isOpen) return null;

  if (isRecordMode && isLoading) {
    return (
      <>
        <div className="pif-modal-overlay"></div>
        <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
          <div className="pif-container" style={{ padding: '30px' }}>
            <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          </div>
        </div>
      </>
    );
  }

  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  const SPECIALITIES = [];

  return (
    <>
      <div className="pif-modal-overlay"></div>
      <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
        <div className="pif-container">
          <div className="pif-stepper-header">
            <button className="pif-modal-close-btn" type="button" onClick={handleClose}><X size={20} /></button>
            <h2 className="pif-stepper-title">{isViewMode ? "View Provider" : isEditMode ? "Edit Provider" : "Add Provider"}</h2>
          </div>

          <div className="pif-content-wrapper">
            <form onSubmit={handleSubmit} noValidate>
              <fieldset disabled={isViewMode} className="pif-view-fieldset">
                <div className="pif-content">
                  <div className="pif-aligned-grid">

                    {/* Personal Info */}
                    <div className="pif-demo-section-label">Personal Information</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-title-group">
                          <label htmlFor="title">Title</label>
                          <select id="title" name="title" className={`pif-input ${errors.title ? "is-invalid" : ""}`} value={formData.title} onChange={handleInputChange}>
                            <option value="">-</option>
                            {TITLES && TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          {errors.title && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.title}</span>}
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="firstName">First Name <span className="text-red-500">*</span></label>
                          <input type="text" id="firstName" name="firstName" className={`pif-input ${errors.firstName ? "is-invalid" : ""}`} value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                          <FormFieldError message={errors.firstName} />
                        </div>
                      </div>
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-mi-group">
                          <label htmlFor="middleInitial">Middle Name</label>
                          <input type="text" id="middleInitial" name="middleInitial" className={`pif-input ${errors.middleInitial ? "is-invalid" : ""}`} value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} />
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="lastName">Last Name <span className="text-red-500">*</span></label>
                          <input type="text" id="lastName" name="lastName" className={`pif-input ${errors.lastName ? "is-invalid" : ""}`} value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                          <FormFieldError message={errors.lastName} />
                        </div>
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="homePhone">Home Phone</label>
                        <PhoneNumberInput id="homePhone" name="homePhone" className={`pif-input ${errors.homePhone ? "is-invalid" : ""}`} value={formData.homePhone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.homePhone && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.homePhone}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="cellPhone">Cell Phone <span className="text-red-500">*</span></label>
                        <PhoneNumberInput id="cellPhone" name="cellPhone" className={`pif-input ${errors.cellPhone ? "is-invalid" : ""}`} value={formData.cellPhone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.cellPhone && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.cellPhone}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="fax">Fax</label>
                        <PhoneNumberInput id="fax" name="fax" className={`pif-input ${errors.fax ? "is-invalid" : ""}`} value={formData.fax} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.fax && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.fax}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="username">Username <span className="text-red-500">*</span></label>
                        <input type="text" id="username" name="username" className={`pif-input ${errors.username ? "is-invalid" : ""}`} value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} />
                        <FormFieldError message={errors.username} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="email">Email <span className="text-red-500">*</span></label>
                        <input type="email" id="email" name="email" className={`pif-input ${errors.email ? "is-invalid" : ""}`} value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., provider@example.com" required />
                        <FormFieldError message={errors.email} />
                      </div>
                    </div>

                    {!isViewMode && (
                      <div className="pif-form-row-balanced">
                        <div className="pif-form-group">
                          <label htmlFor="password">Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showPassword ? "text" : "password"} id="password" name="password" className={`pif-input ${errors.password ? "is-invalid" : ""}`} value={formData.password} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" onFocus={() => setShowPasswordCriteria(true)} onBlur={() => setShowPasswordCriteria(false)} style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={togglePasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                            {showPasswordCriteria && (
                              <div className="ap-password-criteria-block">
                                <div className={`ap-criteria-item ${formData.password.length >= 8 ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={formData.password.length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                                </div>
                                <div className={`ap-criteria-item ${/[A-Z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[A-Z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[a-z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[a-z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[0-9]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[0-9]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                                </div>
                                <div className={`ap-criteria-item ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character
                                </div>
                              </div>
                            )}
                          </div>
                          {errors.password && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.password}</span>}
                        </div>
                        <div className="pif-form-group">
                          <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showConfirmPassword ? "text" : "password"} id="confirmPassword" name="confirmPassword" className={`pif-input ${errors.confirmPassword ? "is-invalid" : ""}`} value={formData.confirmPassword} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={toggleConfirmPasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                            </button>
                          </div>
                          {errors.confirmPassword && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
                        </div>
                      </div>
                    )}

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine1" style={{ marginBottom: 0 }}>Address Line 1 <span className="text-red-500">*</span></label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine1?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine1" name="addressLine1" maxLength={250} className={`pif-input ${errors.addressLine1 ? "is-invalid" : ""}`} value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
                        <FormFieldError message={errors.addressLine1} />
                      </div>
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine2" style={{ marginBottom: 0 }}>Address Line 2</label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine2?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine2" name="addressLine2" maxLength={250} className={`pif-input ${errors.addressLine2 ? "is-invalid" : ""}`} value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="city">City <span className="text-red-500">*</span></label>
                        <input type="text" id="city" name="city" className={`pif-input ${errors.city ? "is-invalid" : ""}`} value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                        <FormFieldError message={errors.city} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="state">State <span className="text-red-500">*</span></label>
                        <select id="state" name="state" className={`pif-input ${errors.state ? "is-invalid" : ""}`} value={formData.state} onChange={handleInputChange} required>
                          <option value="">- Select State -</option>
                          {US_STATES && US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <FormFieldError message={errors.state} />
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="zipCode">Zip Code <span className="text-red-500">*</span></label>
                        <input type="text" id="zipCode" name="zipCode" className={`pif-input ${errors.zipCode ? "is-invalid" : ""}`} value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                        {errors.zipCode && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.zipCode}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="timeZone">Time Zone <span className="text-red-500">*</span></label>
                        <select id="timeZone" name="timeZone" className={`pif-input ${errors.timeZone ? "is-invalid" : ""}`} value={formData.timeZone} onChange={handleInputChange} required>
                          <option value="">Select Time Zone</option>
                          {TIME_ZONES && TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                        {errors.timeZone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.timeZone}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="status">Status <span className="text-red-500">*</span></label>
                        <select id="status" name="status" className={`pif-input ${errors.status ? "is-invalid" : ""}`} value={formData.status} onChange={handleInputChange}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        <FormFieldError message={errors.status} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="twoWayAuth">Two Way Authentication <span className="text-red-500">*</span></label>
                        <select id="twoWayAuth" name="twoWayAuth" className={`pif-input ${errors.twoWayAuth ? "is-invalid" : ""}`} value={formData.twoWayAuth || 'Disabled'} onChange={handleInputChange}>
                          <option value="Disabled">Disabled</option>
                          <option value="Enabled">Enabled</option>
                        </select>
                        <FormFieldError message={errors.twoWayAuth} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="mobileAppAccess">Mobile App Access</label>
                        <select id="mobileAppAccess" name="mobileAppAccess" className="pif-input" value={formData.mobileAppAccess || 'Disabled'} onChange={handleInputChange}>
                          <option value="Disabled">Off</option>
                          <option value="Enabled">On</option>
                        </select>
                      </div>
                    </div>

                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <div className="pif-footer">
            {editId ? (
              <button
                type="button"
                className="pif-events-log-btn"
                onClick={() => setShowEventLogsModal(true)}
                title="View Provider Event Logs"
              >
                <FontAwesomeIcon icon={faList} className="me-2" />
                Event Logs
              </button>
            ) : (
              <span />
            )}
            {!isViewMode && (
              <button type="button" className="pif-save-btn" disabled={isSubmitting || (isEditMode && !isChanged)} onClick={handleSubmit}>
                <Save size={18} />
                {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Provider' : 'Save Provider')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showEventLogsModal && editId && (
        <div ref={logOverlayRef} className="practices-list-modal-overlay" style={{ zIndex: 110000 }}>
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">
                {formData.firstName || formData.lastName
                  ? `${formData.firstName} ${formData.lastName} — Event Logs`
                  : 'Provider Event Logs'}
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowEventLogsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection moduleId="Provider" targetUserId={editId} practiceId={practiceId} />
            </div>
          </div>
        </div>
      )}

      {/* Validation Warning Modal */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered className="add-provider-validation-modal">
        <Modal.Body className="add-provider-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-provider-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <Info size={40} color="#1A252F" />
          </div>
          <h4 className="add-provider-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button type="button" className="add-provider-modal-btn" onClick={() => setShowValidationModal(false)} style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}>
            OK
          </button>
        </Modal.Body>
      </Modal>
      {/* Reassignment Modal */}
      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => {
          setShowReassignmentModal(false);
          setFormData((prev) => ({ ...prev, status: 'Active' }));
        }}
        title="Reassign Patients"
        description={`Select a new provider for each of the ${assignedPatients.length} patient(s) before inactivation.`}
        patients={assignedPatients}
        alternatives={alternativeProviders}
        reassignments={reassignments}
        onReassignmentChange={(patientId, providerId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: providerId }))
        }
        onAssignAll={(providerId) =>
          setReassignments((prev) => {
            const next = { ...prev };
            assignedPatients.forEach((patient) => {
              next[patient.id] = providerId;
            });
            return next;
          })
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select provider..."
        currentLabel="Current provider"
        reassignToLabel="Reassign to provider"
        getCurrentAssignee={(patient) =>
          patient.provider_name ||
          patient.assigned_to_name ||
          patient.current_provider_name ||
          ((patient.provider_first_name || patient.provider_last_name)
            ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
            : '') ||
          'N/A'
        }
      />
    </>
  );
};


export default AddProviderModal;


export { AddProviderModal };
